import React, {useEffect, useState} from 'react';
import {Box, Button, Typography, TextField, Grid} from '@mui/material';
import {ErrorRounded} from '@mui/icons-material';
import {useBff, useTrovoConfig} from '../../utils/config';
import {capitalize} from '../../utils/helpers';
import {IExperimentDetails} from '../../types/experiment';

interface EditExperimentProps {
    /**
     * The experiment to edit.
     */
    experiment: IExperimentDetails;
    /**
     * Callback after succesfully saving user's changes.
     */
    onSave: () => void;
}

/**
 * Turns database representation of some experiment properties to a
 * human-readable string.
 */
const humanReadable = (property: string, value: string) => {
    if (property === 'sequencing_sense') {
        switch (value) {
            case 'F':
            case 'FR':
                return 'Forward';
            case 'R':
            case 'RF':
                return 'Reverse';
            case '':
            case 'Unstranded':
                return 'Unstranded';
        }
    }
    if (property === 'sequencing_read_type') {
        switch (value) {
            case 'paired':
                return 'Paired-end';
            case 'singled':
                return 'Single-end';
            case '':
                return 'N/A';
        }
    }
    return value || 'N/A';
};

/**
 * Component to edit an experiment's name or description. Other properties
 * should not be changed.
 */
const EditExperiment: React.FC<EditExperimentProps> = ({experiment, onSave}) => {
    const [experimentName, setExperimentName] = useState(experiment?.name);
    const [experimentDescription, setExperimentDescription] = useState(experiment?.description);
    const [shouldSave, setShouldSave] = useState(false);
    const {user} = useTrovoConfig();
    const [backendData, backendError] = useBff(
        // TODO: Passing '' as method weirdly "works" here. It gets passed to
        // Window.fetch, which just errors because that is not allowed as a
        // method. Clearly not the way we _want_ this to be implemented, even
        // if the final result is what we want.
        shouldSave ? 'PUT' : '',
        `experiment/${experiment.id}`,
        JSON.stringify({name: experimentName, description: experimentDescription})
    );
    const nameError = experimentName.trim() === '' ? 'Name cannot be empty' : '';

    useEffect(() => {
        console.log(backendData, backendError);
        if (backendData) {
            onSave();
        }
        if (backendError) {
            console.error('Error saving data:', backendError.error);
            setShouldSave(false);
        }
    }, [backendData, backendError]);

    const {
        organism,
        analyzed_molecule,
        rna_selection_method,
        platform_model,
        sequencing_adapter,
        sequencing_platform,
        sequencing_read_type,
        sequencing_sense,
        sequencing_type
    } = experiment;
    const RNAFields = {
        organism,
        rna_selection_method,
        analyzed_molecule,
        platform_model,
        sequencing_adapter,
        sequencing_platform,
        sequencing_read_type,
        sequencing_sense,
        sequencing_type
    };
    const ChIPFields = {
        organism,
        platform_model,
        sequencing_platform,
        sequencing_read_type,
        sequencing_sense,
        sequencing_type
    };
    const fieldsToUse = experiment?.sequencing_type === 'ChIP Seq' ? ChIPFields : RNAFields;

    return (
        <Box style={{padding: '1.5% 4%'}}>
            <Typography variant="headline" size="medium" mb={2} pb={1} sx={{borderBottom: '1px solid', borderColor: 'outline'}}>
                Edit Experiment
            </Typography>
            <Typography variant="title" size="large" pb={2}>
                Experiment Details
            </Typography>
            <Box mb={2} component="form">
                <Typography mb={1}>Experiment Name</Typography>
                <TextField
                    id="experimentName"
                    disabled={user.cb_item_price_id?.includes('demo') || experiment.id === '4015c7cc-1982-4bd4-ae1c-140a7aa6fba9'}
                    value={experimentName}
                    onChange={e => setExperimentName(e.target.value)}
                    fullWidth
                    inputProps={{maxLength: 128}}
                    error={Boolean(nameError)}
                    helperText={nameError}
                />
            </Box>
            <Typography mb={1}>Description (optional)</Typography>
            <TextField
                id="experimentDescription"
                value={experimentDescription}
                multiline
                disabled={user.cb_item_price_id?.includes('demo') || experiment.id === '4015c7cc-1982-4bd4-ae1c-140a7aa6fba9'}
                fullWidth
                maxRows={5}
                onChange={e => setExperimentDescription(e.target.value)}
                inputProps={{maxLength: 8096}}
            />

            <Box mt={2}>
                <Box display="flex" alignItems="center">
                    <Box
                        mb={1}
                        sx={{
                            bgcolor: 'tertiary-container',
                            padding: '2%',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '15px'
                        }}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <ErrorRounded />
                        </Box>
                        <Typography variant="title" size="medium">
                            Note: The following summary selections are locked. Changes to the selections below are only available if you
                            make a duplicate of this Experiment. Once duplicated, your data will be re-processed with updated parameters.
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box mt={2} maxHeight={'317px'}>
                <Grid container spacing={2}>
                    {experiment &&
                        Object.entries(fieldsToUse).map(([k, v]) => (
                            <Grid item sm={6} lg={6} xl={6} key={k}>
                                <TextField
                                    fullWidth
                                    label={k.split('_').map(capitalize).join(' ')}
                                    value={humanReadable(k, v)}
                                    variant="outlined"
                                    disabled
                                />
                            </Grid>
                        ))}
                </Grid>
            </Box>

            <Box mt={2} display="flex" justifyContent="flex-end">
                <Button variant="outlined" onClick={onSave} sx={{borderRadius: '50px', px: 3}}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setExperimentName(experimentName.trim());
                        setExperimentDescription(experimentDescription.trim());
                        setShouldSave(true);
                    }}
                    sx={{marginLeft: '16px', borderRadius: '90px', px: 3}}
                    disabled={
                        user.cb_item_price_id?.includes('demo') ||
                        experiment.id === '4015c7cc-1982-4bd4-ae1c-140a7aa6fba9' ||
                        Boolean(nameError)
                    }>
                    Save & Update
                </Button>
            </Box>
        </Box>
    );
};

export default EditExperiment;
